import { isTemRightPackage } from "@/constants/vip";
import { checkUserGroup, getGrocery } from "@/services";
import { getExpsValue } from "@/tracker/abTest";
import formatTime from "@/utils/formatTime";
import dayjs from "dayjs";
import { at, flatten, get, last, round, trimEnd } from "lodash";

/**
 * 是否是24H加油包
 * @param {*} sku
 */
export const isTemPkg = function (sku) {
  if (sku.right_packages) {
    return sku.right_packages.some((item) =>
      isTemRightPackage(item.identification)
    );
  }

  return false;
};

export function getRightPkgCode(good) {
  return at(good, "skus[0].right_packages[0].identification")[0];
}

export function getSkuRightPkgCode(sku) {
  return at(sku, "right_packages[0].identification")[0];
}

/**
 * 添加商品时，将商品转化成支结算明细项目
 * @param {*} good 商品信息
 * @param {*} number 商品数量
 * @param {{
 *  start,
 *  end,
 *  extend,
 * }} option 扩展 start: 开始计算生效的起始时间
 * @returns
 */
export function getPayDetailByGood(
  good,
  number = 1,
  { start, end, isUpgrade, extend = {}, skuCode, sku } = {}
) {
  const start_at = start ? new Date(start) : new Date();

  if (!sku) {
    sku = skuCode
      ? good.skus.find((sku) => sku.custom_code === skuCode)
      : good.skus[0];
  }

  const upgradeYear = end
    ? dayjs(end).diff(dayjs(new Date()), "y", true).toFixed(2)
    : 0;

  const detail = {
    code: good.code,
    price: isUpgrade
      ? round(
          (sku.price / 365) *
            dayjs(end).diff(dayjs(Date.now()), "days", true).toFixed(0),
          2
        )
      : sku.price,
    name: sku.name,
    skuId: sku.id,
    number: number,
    good: good,
    startTime: start_at,
    years: isUpgrade && upgradeYear > 1 && upgradeYear,
    expire: end || getExpireTime(start_at, sku, number),
    expireTime: end
      ? formatTime(new Date(end), "YYYY/MM/DD")
      : formatTime(getExpireTime(start_at, sku, number), "YYYY/MM/DD"),
    ...extend,
  };

  // 24小时加油包只从现在开始
  if (isTemPkg(sku)) {
    detail.startTime = new Date();
    detail.expireTime = formatTime(
      new Date().getTime() + number * 24 * 3600 * 1000,
      "YYYY/MM/DD HH:mm:ss"
    );
  }

  return detail;
}

/**
 * 获取权益列表中最晚的到期时间
 * @param {IUserRight[]} list
 */
export function getLastExpireTime(list) {
  if (list.length) {
    return Math.max(...list.map(({ end_at }) => new Date(end_at)));
  }
  return new Date();
}

/**
 * 获取可购买最大数量的商品
 * 根据会员时间、已买商品最晚过期时间、商品周期来计算最大可购买个数
 * @param {*} hasRightList 已购买的权益列表
 * @param {number | string} vip_expire 对应已有会员信息
 * @param {*} sku
 */
export function getMaxBuyNumber(hasRightList, vip_expire, sku) {
  const lastExpireDate = new Date(getLastExpireTime(hasRightList));
  const vipExpireDate = new Date(vip_expire);

  const { unit, num, err } = parseSkuDuration(sku);

  if (err) return err;

  const diff = dayjs(vipExpireDate).diff(dayjs(lastExpireDate), unit, true);

  return Math.floor(diff / num);
}

/**
 * 解析商品周期，方便计算
 * @param {*} sku
 * @returns
 */
export function parseSkuDuration(sku) {
  // 不存在商品周期则返回0
  const skuDuration = sku?.props?.duration?.value;
  // return 0
  if (!skuDuration)
    return {
      err: "不存在商品周期",
    };

  const unit = last(skuDuration);
  const num = +trimEnd(skuDuration, unit);

  return {
    unit,
    num,
  };
}
/**
 * 解析商品周期单位，方便显示， 1年显示：年，3个月显示：季， 1个月显示月， 其他则显示数字加单位
 * @param {IGoodSku} sku
 * @returns
 */
export function getSkuUnit(sku) {
  const { unit, num } = parseSkuDuration(sku);

  switch (unit) {
    case "M":
      if (num === 1) return "月";
      if (num === 3) return "季";
      if (num === 12) return "年";
      return num + "月";
    case "d":
      return (num === 1 ? "" : num) + "天";
    case "Y":
      return (num === 1 ? "" : num) + "年";
    default:
      return "";
  }
}

export function getExpireTime(start_at, sku, quantity) {
  const { unit, num, err } = parseSkuDuration(sku);
  if (err) return err;

  return dayjs(start_at)
    .add(num * quantity, unit)
    .toDate();
}
/**
 * 获取升级会员时，会员有效时间
 * @param {*} vips 当前生效会员信息
 * @param {*} sku 商品信息
 * @returns
 */
export function getVipUpgradeExpireTime(sku) {
  const { unit, num } = parseSkuDuration(sku);

  return dayjs().add(num, unit).toDate();
}

/**
 * 获取购买会员扩展包（升级包、扩展授权包）时的过期时间
 * @param {*} vips 当前生效会员信息
 * @param {*} sku 商品信息
 * @param {*} rights 已购买在生效范围内的权益包列表
 * @param {*} number 购买数量
 * @returns {Date}
 */
export function getVipPkgExpireTime(sku, rights, number) {
  const right_package_code = get(sku, "right_packages[0].identification");

  let list = [];
  if (right_package_code) {
    // 匹配同权益的生效权益
    list = rights.filter(
      (item) => item.right_package_code === right_package_code
    );
  }

  return getExpireTime(getLastExpireTime(list), sku, number);
}

/**
 * 获取购买会员扩展包（24小时加油包）时的过期时间
 * @param {*} sku 商品信息
 */
export function getVipTemExpireTime(sku) {
  const { unit, num, err } = parseSkuDuration(sku);
  if (err) return err;
  return dayjs().add(num, unit).toDate();
}

export function isExpVip(user) {
  return user?.extra.material_vip === 5;
}

/**
 * 解析code，返回skus集合
 * @param {IGood[]} goods
 * @param {string} codes 多个用逗号分隔，若要指明sku，请按此格式填写： '商品code.skuCode'
 * @returns {(IGoodSku&{good: IGood})[]}
 */
export function getSkusByCode(goods, codes) {
  if (!codes) return [];

  const codeList = codes.split(",");
  const skus = codeList.map((code) => {
    const goodCode = code.split(".")[0];
    const skuCode = code.split(".")[1];
    const findGood = goods.find((g) => g.code === goodCode);
    let sku;
    if (skuCode) {
      sku = findGood?.skus.find((sku) => sku.custom_code === skuCode);
    } else {
      sku = goods.find((g) => g.code === goodCode)?.skus[0];
    }

    if (sku) {
      return { ...sku, good: findGood };
    } else {
      console.warn(`未找到code：${code} 的商品，请检查配置`);
    }
  });

  if (skus.some((sku) => !sku)) {
    console.warn(`${codes} 部分商品没有找到, 请检查配置`);
    return [];
  }

  return skus;
}

export function getPrice(skus, activity) {
  const activitySkus = activity ? activity.detail.subscribe_goods : [];
  return skus.reduce((pre, cur) => {
    const activityPrice = activitySkus.find(
      (sku) => sku.sku_id == cur.id
    )?.price;

    return pre + (activityPrice === undefined ? cur.price : activityPrice);
  }, 0);
}

export function getOrigrinPrice(skus, activity, suffix = 0, base = 0) {
  const nowPrice = getPrice(skus, activity);
  const originalPrice = skus.reduce((pre, cur) => pre + cur?.original_price, 0);

  return nowPrice < originalPrice ? suffix + (originalPrice + base) : null;
}

/**
 * 添加商品时，将商品转化成支结算明细项目
 * @param {*} skus 商品信息
 * @param {*} number 商品数量
 * @param {{
 *  start?: string|Date|number,
 *  end?: sany,
 *  extend?: any,
 *  rightPkgCodeMap?: any
 * }} option 扩展 start: 开始计算生效的起始时间
 * @returns {IPayDetail[]}
 */
export function getPayDetailBySkus(
  skus,
  number = 1,
  { start, end, extend = {}, rightPkgCodeMap = {} } = {}
) {
  return skus.map((sku) => {
    let start_at = start ? new Date(start) : new Date();
    const good = sku.good;
    const rightPkgCode = getSkuRightPkgCode(sku);

    // 非会员商品的有效时间，需要从此商品权益的过期时间进行计算
    if (good.type !== "vip") {
      start_at = new Date(rightPkgCodeMap[rightPkgCode]?.end_at || Date.now());
    }

    const detail = {
      code: good.code,
      // 升级场景下进行价格折算，总价折算成天的价格，然后与实际购买天数相乘
      price: sku.price,
      // 明细显示商品名称
      name: sku.name,
      // 商品id，支付所需参数
      skuId: sku.id,
      // 购买数量，支付所需参数
      number: number,
      // 对应的商品
      good: good,
      // 开始生效时间，明细进行展示
      startTime: start_at,
      // 过期时间
      expire: end || getExpireTime(start_at, sku, number),
      // 过期日期，明细进行展示
      expireTime: end
        ? formatTime(new Date(end), "YYYY/MM/DD")
        : formatTime(getExpireTime(start_at, sku, number), "YYYY/MM/DD"),
      ...extend,
    };

    // 24小时加油包只从现在开始
    if (isTemPkg(sku)) {
      detail.startTime = new Date();
      detail.expireTime = formatTime(
        new Date().getTime() + number * 24 * 3600 * 1000,
        "YYYY/MM/DD HH:mm:ss"
      );
    }

    return detail;
  });
}

/**
 * 获取杂货店弹窗配置
 * @param {string} configKey 杂货店配置
 * @returns
 */
export async function getDialogConfig(configKey) {
  const result = await getGrocery(configKey);

  if (result.cardsByGroup.length > 0) {
    const group_ids = flatten(result.cardsByGroup.map((item) => item.groupIds));

    const groupIdMap = await checkUserGroup({
      group_ids,
    });

    // 用户分群对应的展示策略
    const matched = result.cardsByGroup.find((item) =>
      item.groupIds.some((id) => groupIdMap[id])
    );

    if (matched) {
      return {
        ...result,
        ...matched,
      };
    }
  }

  // ab实验
  if (result.abtest) {
    const ab = await getExpsValue(result.abtest);

    const matched = result.cardsByABtest.find((item) => item.abValue === ab);

    if (matched) {
      return {
        ...result,
        ...matched,
      };
    }
  }

  return result;
}

// 获取下载报价单链接
export const getQuotationUrl = (
  currentCardInfo,
  calculateRes,
  version,
  selectedPkg,
  plusSkusList = []
) => {
  if (!currentCardInfo || !calculateRes || !version) {
    return;
  }

  const expired = dayjs(calculateRes?.skus[0].trade.expired_at);
  const lastExpired = dayjs(calculateRes?.skus[0].trade.last_expired_at);
  const diffDays = expired.diff(lastExpired, "day");

  let downloadNum = 0;
  // 如果可选每天下载量
  if (currentCardInfo.selectedPkgs?.length) {
    downloadNum =
      selectedPkg ||
      currentCardInfo.selectedPkgs?.[0]?.text ||
      currentCardInfo.selectedPkgs?.[0]?.label;
  } else {
    // 选不了就只能从杂货铺的配置里取
    downloadNum = currentCardInfo.downloadNum;
  }
  // 加购商品
  const plusSkusName = plusSkusList.map((i) => i.groceryName).join(",");

  const queryParams = new URLSearchParams({
    download: "true",
    price: calculateRes?.actual_total_price,
    time: diffDays,
    type: version.key,
    num: parseInt(downloadNum),
    name: version.name,
    ...(plusSkusName
      ? {
          plusSkusName,
        }
      : null),
  });
  return `/quotationPdf?${queryParams.toString()}`;
};
